import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';
import { HelmetDatoCms } from 'gatsby-source-datocms';
import { ReactLenis } from '@studio-freight/react-lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Header from './header';
import '../styles/styles.scss';
import Footer from './footer';
import { BookingOverlay } from './bookings-overlay';
import NewsletterModal from './newsletter-modal';
import CookieBanner from './cookie-banner';
import { ScrollToHash } from './scroll-to-has';
import AnnouncementBar from './announcement-bar';
import HeaderDE from './headerDE';
import ScrollManager from './scroll-manager';
gsap.registerPlugin(ScrollTrigger);

const IS_NON_PRODUCTION_DEPLOY =
	process.env.CONTEXT && process.env.CONTEXT !== 'production';

const Layout = ({ children }) => {
	const locale = process.env.GATSBY_SITE_LOCALE;
	const IS_DE = locale === 'de';
	const data = useStaticQuery(graphql`
		{
			siteEN: datoCmsSite(locale: "en") {
				favicon: faviconMetaTags {
					...GatsbyDatoCmsFaviconMetaTags
				}
				globalSeo {
					siteName
					facebookPageUrl
					fallbackSeo {
						description
						image {
							url
						}
						title
						twitterCard
					}
				}
			}
			siteDE: datoCmsSite(locale: "de") {
				favicon: faviconMetaTags {
					...GatsbyDatoCmsFaviconMetaTags
				}
				globalSeo {
					siteName
					facebookPageUrl
					fallbackSeo {
						description
						image {
							url
						}
						title
						twitterCard
					}
				}
			}
			datoCmsIndex {
				seoMetaTags {
					...GatsbyDatoCmsSeoMetaTags
				}
			}
			config: datoCmsConfig {
				featureFlagNewsletter
				globalSchema
			}
			announcementBar: datoCmsAnnouncementBar {
				show
				background
				time
				content {
					blocks
					links
					value
				}
			}
		}
	`);
	const metaFavicon = IS_DE ? data.siteDE.favicon : data.siteEN.favicon;
	const metaSite = data.datoCmsIndex.seoMetaTags;
	const fallback = IS_DE
		? data.siteDE.globalSeo.fallbackSeo
		: data.siteEN.globalSeo.fallbackSeo;
	const { config, announcementBar } = data;
	const globalSchema = config?.globalSchema?.trim();

	useEffect(() => {
		const resizeObserver = new ResizeObserver((entries) =>
			ScrollTrigger.refresh(),
		);

		resizeObserver.observe(document.body);

		// clean up the observer on unmount
		return () => {
			resizeObserver.disconnect();
		};
	}, []);

	return (
		<React.Fragment key='layout'>
			<Helmet>
				<html lang={locale || 'en'} />
				<meta charSet='utf-8' />
				{IS_NON_PRODUCTION_DEPLOY && (
					<meta name='robots' content='noindex, nofollow' />
				)}
				<link rel='stylesheet' href='//onsass.designmynight.com' />
				<title>{fallback.title}</title>
				<meta name='description' content={fallback.description} />

				<meta property='og:title' content={fallback.title} />
				<meta
					property='og:description'
					content={fallback.description}
				/>
				<meta property='og:type' content='website' />
				<meta
					property='og:url'
					content={
						process.env.GATSBY_SITE_URL ||
						'https://www.pizzapilgrims.co.uk'
					}
				/>
				<meta
					property='og:site_name'
					content={fallback.siteName || 'Pizza Pilgrims'}
				/>
				<meta
					property='og:locale'
					content={locale === 'de' ? 'de_DE' : 'en_GB'}
				/>

				<meta property='og:image' content={fallback.image.url} />
				<meta
					property='og:image:secure_url'
					content={fallback.image.url}
				/>
				<meta property='og:image:width' content='1200' />
				<meta property='og:image:height' content='630' />
				<meta
					property='og:image:alt'
					content={fallback.image.alt || fallback.title}
				/>

				<meta name='twitter:card' content='summary_large_image' />
				<meta name='twitter:title' content={fallback.title} />
				<meta
					name='twitter:description'
					content={fallback.description}
				/>
				<meta name='twitter:image' content={fallback.image.url} />
				{globalSchema && (
					<script type='application/ld+json'>{globalSchema}</script>
				)}
			</Helmet>
			<HelmetDatoCms favicon={metaFavicon} seo={metaSite} />
			<ReactLenis
				root
				options={{
					duration: 1.2,
					easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
					smoothWheel: true,
					wheelMultiplier: 1,
					smoothTouch: false,
					touchMultiplier: 2,
					infinite: false,
				}}
				key='body'
			>
				{!IS_DE && (
					<>
						{announcementBar.show && (
							<AnnouncementBar data={announcementBar} />
						)}
						<Header />
					</>
				)}
				<CookieBanner />
				{IS_DE && <HeaderDE />}
				{children}
				<Footer />
				{!IS_DE && <BookingOverlay />}
				{!IS_DE && config.featureFlagNewsletter && <NewsletterModal />}
				<ScrollToHash />
				<ScrollManager />
			</ReactLenis>
		</React.Fragment>
	);
};
export default Layout;
